import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Section, Cell, Input, Switch, Button, Spinner, Caption, Banner,
} from "@telegram-apps/telegram-ui";
import { api } from "../../lib/api.js";
import { usePageBack } from "../../lib/telegram.js";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ru-RU"); } catch { return iso; }
}

export default function AirfieldDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  usePageBack("/superadmin/airfields");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // local form state
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [classForm, setClassForm] = useState({});
  const [newAdminId, setNewAdminId] = useState("");
  const [newMemberId, setNewMemberId] = useState("");
  const [adminError, setAdminError] = useState(null);
  const [memberError, setMemberError] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);

  function reload() {
    setData(null);
    api(`/api/v1/superadmin/airfields/${slug}`)
      .then((d) => {
        setData(d);
        setStart(d.airfield.day_window_start?.slice(0, 5) || "");
        setEnd(d.airfield.day_window_end?.slice(0, 5) || "");
        setAnonymous(!!d.airfield.anonymous_schedule);
        const cf = {};
        for (const c of d.classes) {
          cf[c.class_code] = {
            enabled: c.enabled,
            max_concurrent: c.max_concurrent ?? "",
            lookahead_days: c.lookahead_days,
          };
        }
        setClassForm(cf);
      })
      .catch((e) => setError(e.message));
  }
  useEffect(reload, [slug]);

  async function saveFields() {
    setBusy(true);
    setError(null);
    try {
      const patch = {
        fields: {
          day_window_start: start,
          day_window_end: end,
          anonymous_schedule: anonymous,
        },
        classes: Object.entries(classForm).map(([class_code, v]) => ({
          class_code,
          enabled: v.enabled,
          max_concurrent: v.max_concurrent === "" ? null : Number(v.max_concurrent),
          lookahead_days: Number(v.lookahead_days),
        })),
      };
      await api(`/api/v1/superadmin/airfields/${slug}`, {
        method: "PATCH", body: JSON.stringify(patch),
      });
      reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function appoint() {
    const q = newAdminId.trim();
    if (!q) { setAdminError("Введите @username или Telegram ID."); return; }
    setBusy(true);
    setAdminError(null);
    try {
      await api(`/api/v1/superadmin/airfields/${slug}/admins`, {
        method: "POST", body: JSON.stringify({ query: q }),
      });
      setNewAdminId("");
      reload();
    } catch (e) { setAdminError(e.message); }
    finally { setBusy(false); }
  }

  async function revokeAdmin(userId) {
    if (!window.confirm("Снять права администратора?")) return;
    setBusy(true);
    try {
      await api(`/api/v1/superadmin/airfields/${slug}/admins/${userId}`, { method: "DELETE" });
      reload();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function addMember() {
    const q = newMemberId.trim();
    if (!q) { setMemberError("Введите @username или Telegram ID."); return; }
    setBusy(true);
    setMemberError(null);
    try {
      await api(`/api/v1/superadmin/airfields/${slug}/members`, {
        method: "POST", body: JSON.stringify({ query: q }),
      });
      setNewMemberId("");
      reload();
    } catch (e) { setMemberError(e.message); }
    finally { setBusy(false); }
  }

  async function copyInvite() {
    const text = data?.invite?.message;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2500);
    } catch {
      // clipboard may be blocked (insecure context or denied permission) —
      // fall through to a textarea selection so the admin can still grab it.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setInviteCopied(true); setTimeout(() => setInviteCopied(false), 2500); }
      catch { setError("Не удалось скопировать. Скопируйте текст вручную из карточки выше."); }
      finally { document.body.removeChild(ta); }
    }
  }

  async function revokeMember(userId) {
    if (!window.confirm("Снять участника?")) return;
    setBusy(true);
    try {
      await api(`/api/v1/superadmin/airfields/${slug}/members/${userId}`, { method: "DELETE" });
      reload();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  if (error && !data) return <Section><Cell multiline>Ошибка: {error}</Cell></Section>;
  if (!data) return <Section><Cell><Spinner size="m" /></Cell></Section>;

  return (
    <>
      <Banner type="section" header={data.airfield.name} subheader={`slug: ${slug}`}
        description={`Создан ${formatDate(data.airfield.created_at)}`}>
        <Button mode="plain" onClick={() => navigate(-1)}>← Назад</Button>
      </Banner>

      {error && <Section><Cell multiline>Ошибка: {error}</Cell></Section>}

      <Section header="Окно полётного дня (МСК)">
        <div style={{ padding: "0 16px", display: "grid", gap: 8 }}>
          <Input header="Начало (HH:MM)" value={start} onChange={(e) => setStart(e.target.value)} />
          <Input header="Конец (HH:MM)" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
        <Cell after={<Switch checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />}
          subtitle="Скрыть имена пилотов в публичном расписании">
          Анонимное расписание
        </Cell>
      </Section>

      {Object.entries(classForm).map(([code, v]) => (
        <Section key={code} header={`Класс ${code}`}>
          <Cell after={
            <Switch checked={v.enabled} onChange={(e) =>
              setClassForm({ ...classForm, [code]: { ...v, enabled: e.target.checked } })}/>
          }>Включён</Cell>
          <div style={{ padding: "0 16px", display: "grid", gap: 8 }}>
            <Input
              header="Макс. одновременно (пусто = без ограничений)"
              value={String(v.max_concurrent ?? "")}
              onChange={(e) =>
                setClassForm({ ...classForm, [code]: { ...v, max_concurrent: e.target.value } })}
            />
            <Input
              header="Горизонт бронирования (дней, 0–14)"
              value={String(v.lookahead_days ?? "")}
              onChange={(e) =>
                setClassForm({ ...classForm, [code]: { ...v, lookahead_days: e.target.value } })}
            />
          </div>
        </Section>
      ))}

      <Section>
        <div style={{ padding: "0 16px" }}>
          <Button onClick={saveFields} disabled={busy} stretched>
            {busy ? "Сохраняем..." : "Сохранить настройки"}
          </Button>
        </div>
      </Section>

      {data.invite && (
        <Section header="Пригласить пилота">
          <Cell multiline>
            <Caption level="1">
              Отправьте эту ссылку новому пилоту. Когда он откроет бота, я получу уведомление,
              и его можно будет добавить в члены аэродрома одним тапом.
            </Caption>
          </Cell>
          <div style={{ padding: "0 16px" }}>
            <div style={{
              background: "var(--tgui--secondary_bg_color, #f5f5f5)",
              borderRadius: 8,
              padding: 12,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 13,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              color: "var(--tgui--text_color, #000)",
            }}>{data.invite.message}</div>
          </div>
          <div style={{ padding: "8px 16px" }}>
            <Button onClick={copyInvite} stretched>
              {inviteCopied ? "✓ Скопировано" : "Скопировать приглашение"}
            </Button>
          </div>
        </Section>
      )}

      <Section header={`Администраторы (${data.admins.length})`}>
        {data.admins.length === 0 && <Cell><Caption level="1">Нет назначенных админов.</Caption></Cell>}
        {data.admins.map((a) => {
          const fullName = [a.first_name, a.last_name].filter(Boolean).join(" ");
          const handle = a.tg_username ? `@${a.tg_username}` : null;
          const title = fullName || handle || "Без имени в Telegram";
          const subParts = [`#${a.user_id}`];
          if (fullName && handle) subParts.push(handle);
          subParts.push(`с ${formatDate(a.created_at)}`);
          return (
            <Cell key={a.user_id}
              subtitle={subParts.join(" · ")}
              after={<Button mode="plain" onClick={() => revokeAdmin(a.user_id)}>✕</Button>}
              onClick={() => navigate(`/superadmin/users/${a.user_id}`)}
            >🛠 {title}</Cell>
          );
        })}
        <div style={{ padding: "8px 16px", display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <Input
              placeholder="@username или Telegram ID"
              value={newAdminId}
              onChange={(e) => setNewAdminId(e.target.value)}
            />
          </div>
          <Button onClick={appoint} disabled={busy}>Назначить</Button>
        </div>
        {adminError && (
          <div style={{ padding: "0 16px 8px", color: "var(--tgui--destructive_text_color, #d33)" }}>
            <Caption level="1">{adminError}</Caption>
          </div>
        )}
        <Cell><Caption level="1">Введите @username или Telegram ID. Если пилот ещё не запускал бота, мы создадим запись по ID и привяжем профиль, как только он нажмёт /start.</Caption></Cell>
      </Section>

      <Section header={`Участники (${data.members.length})`}>
        {data.members.length === 0 && <Cell><Caption level="1">Нет участников.</Caption></Cell>}
        {data.members.map((m) => {
          const fullName = [m.first_name, m.last_name].filter(Boolean).join(" ");
          const handle = m.tg_username ? `@${m.tg_username}` : null;
          const title = fullName || handle || "Без имени в Telegram";
          const subParts = [`#${m.user_id}`];
          if (fullName && handle) subParts.push(handle);
          if (m.is_admin) subParts.push("админ");
          return (
            <Cell key={m.user_id}
              subtitle={subParts.join(" · ")}
              after={!m.is_admin && (
                <Button mode="plain" onClick={(e) => { e.stopPropagation(); revokeMember(m.user_id); }}>✕</Button>
              )}
              onClick={() => navigate(`/superadmin/users/${m.user_id}`)}
            >{title}</Cell>
          );
        })}
        <div style={{ padding: "8px 16px", display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <Input
              placeholder="@username или Telegram ID"
              value={newMemberId}
              onChange={(e) => setNewMemberId(e.target.value)}
            />
          </div>
          <Button onClick={addMember} disabled={busy}>Добавить</Button>
        </div>
        {memberError && (
          <div style={{ padding: "0 16px 8px", color: "var(--tgui--destructive_text_color, #d33)" }}>
            <Caption level="1">{memberError}</Caption>
          </div>
        )}
      </Section>
    </>
  );
}
